import React, { useState, useContext, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import PrinterStatusIndicator from './PrinterStatusIndicator';
import logo from '../assets/logo.png';

// Pantallas de hasta ~15" (celulares, tablets, terminales POS como Posiflex, laptops de 13-14"):
// menú hamburguesa y vista compacta. La vista completa queda para 16" en adelante (>= 1536px).
export const COMPACT_MAX_WIDTH = 1440;
export const DRAWER_QUERY = `(max-width: ${COMPACT_MAX_WIDTH}px)`;
// Evento para abrir el menú desde pantallas con encabezado propio (ej. Punto de Venta)
export const OPEN_SIDEBAR_EVENT = 'tionel:open-sidebar';
export const openSidebar = () => window.dispatchEvent(new Event(OPEN_SIDEBAR_EVENT));

const TOPBAR_HEIGHT = 56;

const useMediaQuery = (query) => {
    const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
    useEffect(() => {
        const mql = window.matchMedia(query);
        const onChange = () => setMatches(mql.matches);
        onChange();
        mql.addEventListener('change', onChange);
        return () => mql.removeEventListener('change', onChange);
    }, [query]);
    return matches;
};

const BarraLateral = () => {
    const location = useLocation();
    const { user, logout } = useContext(AuthContext);
    const [collapsedPref, setCollapsedPref] = useState(false);
    const isDrawer = useMediaQuery(DRAWER_QUERY);
    const [drawerOpen, setDrawerOpen] = useState(false);
    // En modo hamburguesa el menú siempre se muestra completo (con textos)
    const isCollapsed = !isDrawer && collapsedPref;

    // Pantallas con encabezado propio (no se muestra la barra superior global)
    const hasOwnHeader = location.pathname.startsWith('/fast-food/pos');

    useEffect(() => {
        const open = () => setDrawerOpen(true);
        window.addEventListener(OPEN_SIDEBAR_EVENT, open);
        return () => window.removeEventListener(OPEN_SIDEBAR_EVENT, open);
    }, []);

    // Cerrar el menú al navegar o al pasar a pantalla grande
    useEffect(() => {
        setDrawerOpen(false);
    }, [location.pathname, isDrawer]);

    const isActive = (path) => {
        return location.pathname === path || location.pathname.startsWith(path) ? 'active' : '';
    };

    const toggleSidebar = () => {
        if (isDrawer) setDrawerOpen(false);
        else setCollapsedPref(!collapsedPref);
    };

    const currentItemLabel = () => {
        const match = [...menuItems]
            .sort((a, b) => b.path.length - a.path.length)
            .find(item => location.pathname.startsWith(item.path));
        return match ? match.label : 'TioNel';
    };

    const menuItems = [
        { path: '/inicio', icon: 'bi-house-door-fill', label: 'Inicio' },
        { path: '/fast-food/pos', icon: 'bi-cart-fill', label: 'Punto de Venta' },
        { path: '/fast-food/tables', icon: 'bi-grid-3x3-gap-fill', label: 'Mesas' },
        { path: '/fast-food/tasa-cambio', icon: 'bi-currency-exchange', label: 'Tasa de Cambio' },
        { path: '/fast-food/orders', icon: 'bi-receipt', label: 'Órdenes' },
        { path: '/fast-food/inventory', icon: 'bi-box-seam', label: 'Menú' },
        { path: '/fast-food/toppings', icon: 'bi-stars', label: 'Toppings' },
        { path: '/fast-food/variables', icon: 'bi-sliders', label: 'Variables' },
        { path: '/fast-food/promotions', icon: 'bi-star-fill', label: 'Promociones' },
        { path: '/fast-food/bodega', icon: 'bi-archive-fill', label: 'Bodega (Insumos)' },
        { path: '/fast-food/mezclas', icon: 'bi-arrow-repeat', label: 'Mezclas' },
        { path: '/fast-food/customers', icon: 'bi-person-badge', label: 'Clientes' },
        { path: '/fast-food/shift', icon: 'bi-cash-coin', label: 'Caja' },
        { path: '/fast-food/feria-shift', icon: 'bi-shop', label: 'Caja de Ferias' },
        { path: '/fast-food/gastos', icon: 'bi-wallet2', label: 'Gastos' },
        { path: '/fast-food/facturacion-sri', icon: 'bi-file-earmark-check-fill', label: 'Facturación SRI' },
        { path: '/fast-food/reports', icon: 'bi-graph-up', label: 'Reportes' },
        { path: '/fast-food/periodic-reports', icon: 'bi-calendar-week', label: 'Reportes Semanales' },
        { path: '/fast-food/printers', icon: 'bi-printer-fill', label: 'Impresoras' },
        { path: '/fast-food/secuencia', icon: 'bi-123', label: 'Turno / N° Orden' },
        { path: '/cobranzas', icon: 'bi-cash-stack', label: 'Cobranzas' },
        { path: '/users', icon: 'bi-people-fill', label: 'Usuarios' },
    ];

    // Estilos inline
    const styles = {
        sidebar: {
            position: 'fixed',
            top: 0,
            left: 0,
            height: '100dvh',
            width: isDrawer ? '280px' : (isCollapsed ? '70px' : '250px'),
            maxWidth: '85vw',
            background: 'var(--sidebar-bg)',
            color: 'var(--sidebar-text)',
            transition: isDrawer ? 'transform 0.25s ease' : 'width 0.3s ease',
            transform: isDrawer && !drawerOpen ? 'translateX(-100%)' : 'translateX(0)',
            zIndex: isDrawer ? 10001 : 1000,
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '4px 0 24px rgba(0, 0, 0, 0.05)',
            overflowX: 'hidden',
            borderRight: '1px solid var(--border-color)'
        },
        sidebarHeader: {
            padding: '1.5rem 1rem',
            borderBottom: '1px solid var(--border-color)',
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem'
        },
        brandContainer: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '0.5rem'
        },
        brandLogo: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1,
            minWidth: 0,
            padding: '10px 0'
        },
        brandIcon: {
            display: 'none'
        },
        brandText: {
            display: 'none'
        },
        btnToggle: {
            background: 'var(--sidebar-bg)',
            border: '1px solid var(--border-color)',
            color: 'var(--sidebar-text)',
            width: '36px',
            height: '36px',
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s ease',
            fontSize: '1.1rem',
            flexShrink: 0,
            boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
        },
        userInfo: {
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            padding: '0.75rem',
            background: 'var(--sidebar-bg)',
            borderRadius: '12px',
            border: '1px solid var(--border-color)',
            boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
            opacity: isCollapsed ? 0 : 1,
            height: isCollapsed ? 0 : 'auto',
            overflow: 'hidden',
            transition: 'all 0.3s ease'
        },
        userAvatar: {
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            background: 'var(--secondary-light, #f5e8d3)',
            color: 'var(--primary-color, #603833)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.2rem',
            fontWeight: '600',
            flexShrink: 0
        },
        userDetails: {
            flex: 1,
            minWidth: 0
        },
        userName: {
            fontSize: '0.875rem',
            fontWeight: '600',
            color: 'var(--sidebar-text)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
        },
        userRole: {
            fontSize: '0.75rem',
            color: 'var(--primary-color)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
        },
        sidebarNav: {
            listStyle: 'none',
            padding: '1rem 0',
            margin: 0,
            flex: 1,
            overflowY: 'auto'
        },
        navItem: {
            margin: '0.25rem 0'
        },
        navLink: {
            display: 'flex',
            alignItems: 'center',
            padding: '0.875rem 1.25rem',
            color: 'var(--sidebar-text)',
            textDecoration: 'none',
            transition: 'all 0.3s ease',
            gap: '1rem',
            position: 'relative',
            borderRadius: '8px',
            margin: '0 0.5rem',
            opacity: 0.8
        },
        navLinkActive: {
            background: 'var(--secondary-light, #f5e8d3)',
            color: 'var(--primary-color, #603833)',
            fontWeight: '600',
            opacity: 1
        },
        navIcon: {
            fontSize: '1.25rem',
            minWidth: '24px',
            transition: 'all 0.2s'
        },
        navText: {
            whiteSpace: 'nowrap',
            opacity: isCollapsed ? 0 : 1,
            width: isCollapsed ? 0 : 'auto',
            overflow: 'hidden',
            transition: 'opacity 0.2s'
        },
        sidebarFooter: {
            padding: '1rem',
            borderTop: '1px solid var(--border-color)'
        },
        logoutButton: {
            width: '100%',
            padding: '0.75rem 1rem',
            background: 'var(--sidebar-bg)',
            border: '1px solid var(--border-color)',
            borderRadius: '8px',
            color: '#ef4444',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: isCollapsed ? 'center' : 'flex-start',
            gap: '0.75rem',
            fontSize: '0.875rem',
            fontWeight: '600',
            transition: 'all 0.3s ease'
        },
        versionText: {
            textAlign: 'center',
            fontSize: '0.7rem',
            color: '#64748b',
            marginTop: '0.5rem',
            opacity: isCollapsed ? 0 : 1,
            height: isCollapsed ? 0 : 'auto',
            overflow: 'hidden',
            transition: 'all 0.2s'
        }
    };

    return (
        <>
            {/* Agregar Bootstrap Icons */}
            <link
                rel="stylesheet"
                href="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.3/font/bootstrap-icons.min.css"
            />

            {/* Barra superior con botón hamburguesa (celulares y tablets) */}
            {isDrawer && !hasOwnHeader && (
                <header className="mobile-topbar">
                    <button
                        type="button"
                        className="mobile-topbar-btn"
                        onClick={() => setDrawerOpen(true)}
                        aria-label="Abrir menú"
                        title="Menú"
                    >
                        <i className="bi bi-list"></i>
                    </button>
                    <img src={logo} alt="TioNel" className="mobile-topbar-logo" />
                    <span className="mobile-topbar-title">{currentItemLabel()}</span>
                </header>
            )}

            {/* Fondo oscuro detrás del menú abierto */}
            {isDrawer && drawerOpen && (
                <div className="sidebar-backdrop" onClick={() => setDrawerOpen(false)} />
            )}

            <aside style={styles.sidebar} aria-hidden={isDrawer && !drawerOpen}>
                {/* Header con Logo y Toggle */}
                <div style={styles.sidebarHeader}>
                    <div style={styles.brandContainer}>
                        <div style={styles.brandLogo}>
                            <img src={logo} alt="TioNel" style={{ height: isCollapsed ? '40px' : '120px', transition: 'all 0.3s', objectFit: 'contain', maxWidth: '100%' }} />
                        </div>
                        <button
                            style={styles.btnToggle}
                            onClick={toggleSidebar}
                            title={isDrawer ? 'Cerrar menú' : (isCollapsed ? 'Expandir' : 'Contraer')}
                            onMouseEnter={(e) => {
                                e.target.style.background = '#f1f5f9';
                                e.target.style.transform = 'scale(1.05)';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.background = '#f8fafc';
                                e.target.style.transform = 'scale(1)';
                            }}
                        >
                            <i className={`bi ${isDrawer ? 'bi-x-lg' : (isCollapsed ? 'bi-chevron-right' : 'bi-chevron-left')}`}></i>
                        </button>
                    </div>

                    {/* Info del Usuario */}
                    <div style={styles.userInfo}>
                        <div style={styles.userAvatar}>
                            {user?.username?.charAt(0).toUpperCase() || 'U'}
                        </div>
                        <div style={styles.userDetails}>
                            <div style={styles.userName}>
                                {user?.username || 'Usuario'}
                            </div>
                            <div style={styles.userRole}>
                                {user?.role || 'Administrador'}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Navegación */}
                <ul style={styles.sidebarNav}>
                    {(() => {
                        const roleName = user?.role_details?.name;
                        const isSuperAdmin = roleName === 'SUPER_ADMIN' || user?.is_superuser;
                        const isGestorCobranzas = roleName === 'GESTOR_COBRANZAS';
                        const isAdminFastFood = roleName === 'ADMIN_FAST_FOOD';
                        const isCook = roleName === 'COOK' || user?.role === 'COOK' || user?.role_details?.description?.toLowerCase()?.includes('cocinero');
                        
                        const filteredMenuItems = menuItems.filter(item => {
                            // Si es Gestor de Cobranzas, solo ve su panel de Cobranzas
                            if (isGestorCobranzas) {
                                return ['/cobranzas'].includes(item.path);
                            }
                            
                            // Ningún otro rol (ni Super Admin ni empleados) debe ver el panel de cobranzas
                            if (item.path === '/cobranzas') {
                                return false;
                            }

                            if (isSuperAdmin) return true;
                            if (isAdminFastFood && item.path.startsWith('/fast-food')) return true;
                            
                            // Si es Cocinero (COOK): ve módulos de empleado + Caja de Ferias (sin Bodega)
                            if (isCook) {
                                const cookPaths = [
                                    '/fast-food/pos',
                                    '/fast-food/tables',
                                    '/fast-food/orders',
                                    '/fast-food/inventory',
                                    '/fast-food/shift',
                                    '/fast-food/feria-shift'
                                ];
                                return cookPaths.includes(item.path);
                            }

                            // Para empleados normales de Fast Food (solo POS, Mesas, Órdenes, Menú y Caja)
                            const employeePaths = [
                                '/fast-food/pos',
                                '/fast-food/tables',
                                '/fast-food/orders',
                                '/fast-food/inventory',
                                '/fast-food/shift'
                            ];
                            return employeePaths.includes(item.path);
                        });

                        return filteredMenuItems.map((item) => {
                            const active = isActive(item.path);
                            return (
                                <li key={item.path} style={styles.navItem}>
                                    <Link
                                        to={item.path}
                                        title={isCollapsed ? item.label : ''}
                                        style={{
                                            ...styles.navLink,
                                            ...(active ? styles.navLinkActive : {})
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!active) {
                                                e.currentTarget.style.background = 'var(--secondary-light, #f5e8d3)';
                                                e.currentTarget.style.color = 'var(--primary-color)';
                                                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.02)';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (!active) {
                                                e.currentTarget.style.background = 'transparent';
                                                e.currentTarget.style.color = 'var(--sidebar-text)';
                                                e.currentTarget.style.boxShadow = 'none';
                                            }
                                        }}
                                    >
                                        <i
                                            className={`bi ${item.icon}`}
                                            style={{
                                                ...styles.navIcon,
                                                color: active ? 'var(--primary-color)' : 'var(--sidebar-text)'
                                            }}
                                        ></i>
                                        <span style={styles.navText}>{item.label}</span>
                                    </Link>
                                </li>
                            );
                        });
                    })()}
                </ul>

                {/* Footer con Logout */}
                <div style={styles.sidebarFooter}>
                    {!isCollapsed && <PrinterStatusIndicator />}
                    
                    <button
                        style={{
                            ...styles.logoutButton,
                            marginTop: isCollapsed ? '0.5rem' : '1rem'
                        }}
                        onClick={logout}
                        title="Cerrar Sesión"
                        onMouseEnter={(e) => {
                            e.target.style.background = '#fecaca';
                            e.target.style.borderColor = '#fca5a5';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.background = '#fef2f2';
                            e.target.style.borderColor = '#fee2e2';
                        }}
                    >
                        <i className="bi bi-box-arrow-right" style={{ fontSize: '1.1rem' }}></i>
                        <span style={styles.navText}>Cerrar Sesión</span>
                    </button>

                    <div style={styles.versionText}>
                        v1.0.0
                    </div>
                </div>
            </aside>

            {/* Estilos globales adicionales */}
            <style>{`
                /* Scrollbar personalizado */
                .sidebar-nav::-webkit-scrollbar {
                    width: 4px;
                }
                .sidebar-nav::-webkit-scrollbar-track {
                    background: transparent;
                }
                .sidebar-nav::-webkit-scrollbar-thumb {
                    background: #e2e8f0;
                    border-radius: 4px;
                }
                .sidebar-nav::-webkit-scrollbar-thumb:hover {
                    background: #cbd5e1;
                }

                /* Ajuste para el contenido principal */
                .main-content {
                    margin-left: ${isCollapsed ? '70px' : '250px'};
                    transition: margin-left 0.3s ease;
                    min-height: 100vh;
                    min-width: 0;
                }

                /* Barra superior (solo celulares y tablets) */
                .mobile-topbar {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    height: ${TOPBAR_HEIGHT}px;
                    padding: 0 0.75rem;
                    padding-top: env(safe-area-inset-top);
                    box-sizing: content-box;
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    background: #ffffff;
                    border-bottom: 1px solid var(--border-color);
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
                    z-index: 1000;
                }
                .mobile-topbar-btn {
                    width: 44px;
                    height: 44px;
                    flex-shrink: 0;
                    border-radius: 10px;
                    border: 1px solid #d1d5db;
                    background: var(--sidebar-bg, #faf4eb);
                    color: var(--primary-color, #603833);
                    font-size: 1.5rem;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                }
                .mobile-topbar-logo {
                    height: 40px;
                    object-fit: contain;
                    flex-shrink: 0;
                }
                .mobile-topbar-title {
                    font-size: 1.1rem;
                    font-weight: 700;
                    color: #111827;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
                .sidebar-backdrop {
                    position: fixed;
                    inset: 0;
                    background: rgba(0, 0, 0, 0.5);
                    z-index: 10000;
                    animation: sidebarFadeIn 0.2s ease-out;
                }
                @keyframes sidebarFadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }

                /* Pantallas compactas: contenido a todo el ancho */
                @media ${DRAWER_QUERY} {
                    .main-content {
                        margin-left: 0 !important;
                        padding: ${hasOwnHeader ? '0' : `calc(${TOPBAR_HEIGHT}px + env(safe-area-inset-top) + 12px) 12px 12px`} !important;
                    }
                }
            `}</style>
        </>
    );
};

export default BarraLateral;